import { forwardRef, useImperativeHandle, useMemo, useState } from "react";
import { X, Check } from "react-feather";
import {
  selectAllPlannedMeals,
  selectAllIngredients,
  selectAllRecipes,
  consolidateIngredients,
  selectLocale,
  intlLocale,
  useT,
  useAppSelector,
} from "@reciperoll/core";
import { useToast } from "./Toast";
import { useShoppingListImport } from "../hooks/useShoppingListImport";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const monthDay = (d: Date, intl: string) =>
  d.toLocaleDateString(intl, { day: "numeric", month: "short" });

export type ShopDaysModalRef = { open: () => void };

// "Add to shopping list" day picker — tick the days you're shopping for, then
// consolidate those days' ingredients into the list. Shared by the meal planner
// and the shopping list so both open the identical modal.
export const ShopDaysModal = forwardRef<
  ShopDaysModalRef,
  {
    /** The week's seven days, in order. */
    days: Date[];
    todayYmd: string;
    /** Called after items are added (e.g. to navigate to the shopping list). */
    onAdded?: () => void;
  }
>(function ShopDaysModal({ days, todayYmd, onAdded }, ref) {
  const toast = useToast();
  const { importItems, choiceModal } = useShoppingListImport();
  const allPlanned = useAppSelector(selectAllPlannedMeals);
  const allIngredients = useAppSelector(selectAllIngredients);
  const recipes = useAppSelector(selectAllRecipes);
  const intl = intlLocale(useAppSelector(selectLocale));
  const t = useT();
  const recipesById = useMemo(() => new Map(recipes.map((r) => [r.id, r])), [recipes]);
  const [shopDays, setShopDays] = useState<Set<string> | null>(null);

  const mealCountByDay = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const day of days) counts[ymd(day)] = 0;
    for (const m of allPlanned) {
      if (counts[m.mealDate] === undefined) continue;
      if (!recipesById.has(m.recipeId)) continue; // recipe was deleted
      counts[m.mealDate]++;
    }
    return counts;
  }, [allPlanned, days, recipesById]);

  // Open the day-picker with every day that has meals ticked (confirming as-is
  // reproduces the old whole-week behaviour).
  useImperativeHandle(
    ref,
    () => ({
      open: () => {
        const planned = days.map(ymd).filter((d) => mealCountByDay[d] > 0);
        if (planned.length === 0) {
          toast.show(t("shopping.nothingPlanned"));
          return;
        }
        setShopDays(new Set(planned));
      },
    }),
    [days, mealCountByDay, toast, t],
  );

  const toggleShopDay = (day: string) =>
    setShopDays((prev) => {
      const next = new Set(prev);
      if (next.has(day)) next.delete(day);
      else next.add(day);
      return next;
    });

  // Consolidate the selected days' ingredients into the shopping list.
  const addSelectedDaysToShoppingList = () => {
    if (!shopDays) return;
    const recipeIds = new Set(
      allPlanned.filter((m) => shopDays.has(m.mealDate)).map((m) => m.recipeId),
    );
    const consolidated = consolidateIngredients(
      allIngredients.filter((i) => recipeIds.has(i.recipeId)),
    );
    if (consolidated.length === 0) {
      toast.show(t("shopping.noIngredientsForDays"));
      setShopDays(null);
      return;
    }
    setShopDays(null);
    // Never silently merge — importItems asks if any are already on the list.
    importItems(consolidated, onAdded);
  };

  const planned = days.map(ymd).filter((d) => mealCountByDay[d] > 0);
  const allSelected = !!shopDays && planned.every((d) => shopDays.has(d));

  return (
    <>
      {shopDays && (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => setShopDays(null)}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-1">
          <h2 className="text-xl font-bold text-ink">{t("recipe.addToShoppingList")}</h2>
          <button
            onClick={() => setShopDays(null)}
            aria-label={t("common.close")}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex items-center justify-between mb-3">
          <p className="text-sm text-ink-faint">{t("shopping.chooseDays")}</p>
          <button
            onClick={() => setShopDays(new Set(allSelected ? [] : planned))}
            className="text-sm font-semibold text-terracotta-ink hover:text-terracotta-dark cursor-pointer"
          >
            {allSelected ? t("shopping.clearSelection") : t("shopping.selectAll")}
          </button>
        </div>

        <div className="flex flex-col">
          {days.map((day) => {
            const dYmd = ymd(day);
            const count = mealCountByDay[dYmd] ?? 0;
            const disabled = count === 0;
            const selected = shopDays.has(dYmd);
            const isToday = dYmd === todayYmd;
            return (
              <button
                key={dYmd}
                disabled={disabled}
                onClick={() => toggleShopDay(dYmd)}
                className={`flex items-center gap-3 py-3 text-left ${
                  disabled ? "opacity-40 cursor-default" : "cursor-pointer group"
                }`}
              >
                <span
                  className={`w-6 h-6 shrink-0 rounded-md border-2 flex items-center justify-center transition-colors ${
                    selected
                      ? "bg-terracotta border-terracotta"
                      : "border-line group-hover:border-terracotta"
                  }`}
                >
                  {selected && <Check className="w-3.5 h-3.5 text-white" strokeWidth={3} />}
                </span>
                <span className="flex-1 flex items-baseline gap-2">
                  <span
                    className={`text-[15px] font-semibold ${isToday ? "text-terracotta-ink" : "text-ink"}`}
                  >
                    {day.toLocaleDateString(intl, { weekday: "long" })}
                  </span>
                  <span className="text-sm text-ink-faint">{monthDay(day, intl)}</span>
                </span>
                <span className="text-sm text-ink-faint">
                  {count === 0 ? t("shopping.noMeals") : t("shopping.mealsCount", { count })}
                </span>
              </button>
            );
          })}
        </div>

        <button
          onClick={addSelectedDaysToShoppingList}
          disabled={shopDays.size === 0}
          className={`mt-4 w-full rounded-xl py-3 text-[15px] font-bold transition-colors ${
            shopDays.size === 0
              ? "bg-surface-2 text-ink-faint cursor-default"
              : "bg-terracotta hover:bg-terracotta-dark text-white cursor-pointer"
          }`}
        >
          {shopDays.size > 0
            ? t("shopping.addDaysToList", { count: shopDays.size })
            : t("shopping.selectDaysToAdd")}
        </button>
      </div>
    </div>
      )}
      {choiceModal}
    </>
  );
});
